import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, FileText, Users, AlertCircle, FileCheck,
  RefreshCw, BookOpen, Database, Search, Download } from 'lucide-react';
import { NEU } from '../neuStyles';

/**
 * CompliancePage — admin-facing GDPR / CCPA / HIPAA compliance surface.
 *
 * Wires the 16 IPC handlers in routes/compliance.js into a single page so
 * the scaffolding from Task #177 is visible in the running app. Some of the
 * underlying endpoints are STUBs pending full implementation (Tasks
 * #172-#175); the UI marks those clearly so the gap stays auditable.
 *
 * Channels surfaced:
 *   DSR (data subject rights — GDPR Art. 15/17/20, CCPA 1798.100/105):
 *     compliance:dsr:list                — real
 *     compliance:dsr:create              — real
 *     compliance:dsr:verify-identity     — real
 *     compliance:dsr:execute-erasure     — STUB (Task #172)
 *     compliance:dsr:export-data         — STUB (Task #172)
 *
 *   Breach / incident (GDPR Art. 33, all US state laws):
 *     compliance:incident:list                    — real
 *     compliance:incident:declare                 — real
 *     compliance:incident:update                  — real
 *     compliance:incident:list-affected-subjects  — STUB (Task #173)
 *
 *   Training (MA 201 CMR 17.04(8), CA, CO, CT, GDPR Art. 39):
 *     compliance:training:list           — real
 *     compliance:training:record         — real
 *     compliance:training:check-status   — STUB (Task #174)
 *
 *   RoPA + DPA + Privacy Notice (GDPR Art. 30 + sub-processors):
 *     compliance:dpa:list                       — real
 *     compliance:dpa:upsert                     — real
 *     compliance:ropa:list                      — real
 *     compliance:privacy-notice:list-versions   — real
 */


function Section({ title, icon: Icon, children }) {
  return (
    <div style={{
      background: NEU.color?.cardBg || '#FFFFFF',
      border: `1px solid ${NEU.color?.border || '#E8ECF1'}`,
      borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
        {Icon ? <Icon size={18} color="#2E8B98" /> : null}
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1f2937' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}


function LiveBadge() {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', marginLeft: 6,
      borderRadius: 10, background: '#DCFCE7', color: '#166534',
      fontSize: 11, fontWeight: 600, letterSpacing: 0.3,
    }}>LIVE</span>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div style={{
      background: '#FEE2E2', border: '1px solid #FCA5A5',
      color: '#991B1B', padding: '0.5rem 0.75rem', borderRadius: 8,
      marginBottom: '0.75rem', fontSize: 13,
    }}>
      <strong>Error:</strong> {error}
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div style={{ color: '#6b7280', fontSize: 13, padding: '0.75rem 0' }}>
      {children}
    </div>
  );
}

/*
 * ⛔⛔ ADMIN + SYSADMIN ONLY — the same answer the handlers give.
 *
 * Card P128. Every write channel on this page is secureHandler("admin:create"
 * | "admin:update" | "admin:delete"), and `admin:*` is held by exactly `admin`
 * and `sysadmin`. This mirrors that, so a user who would be refused never sees
 * the form — and the server refuses regardless, which is the actual control.
 */
export const COMPLIANCE_WRITE_ROLES = Object.freeze(['admin', 'sysadmin']);
export function canWriteCompliance(user) {
  return COMPLIANCE_WRITE_ROLES.includes(user && user.role);
}

/*
 * ⚠ TWO LISTS, PINNED TO TWO DIFFERENT AUTHORITIES, AND THEY ARE NOT THE SAME.
 *
 * ALL_CATEGORIES is the DATABASE's own CHECK constraint on
 * workforce_training_records.training_category — anything outside it is
 * refused by Postgres, so the picker must not offer a value that cannot be
 * stored.
 *
 * GATE_CATEGORIES is the subset the LOGIN GATE evaluates
 * (database/trainingGate.js:24). The other four are perfectly legitimate
 * records that clear NOTHING.
 *
 * ⛔ OFFERING ONLY THE GATE THREE WOULD BE THE WRONG FIX. It would make four
 * valid categories unrecordable through the only form that exists. Offering
 * all seven and SAYING which three lift a block is the honest version: an
 * admin fixing a lockout can see which one to pick, and an admin logging
 * onboarding is not blocked from doing so.
 *
 * React cannot import from database/, so both are second copies — and tests
 * assert each against its real authority, which is what stops them drifting.
 */
export const ALL_CATEGORIES = Object.freeze([
  'hipaa_annual', 'gdpr_data_protection', 'ma_201cmr17_annual',
  'security_awareness', 'phishing_simulation', 'role_specific', 'onboarding',
]);
export const GATE_CATEGORIES = Object.freeze([
  'hipaa_annual', 'ma_201cmr17_annual', 'security_awareness',
]);

/** One submit path: calls, reads `error`, and never reports a refusal as done. */
function useAction(onDone) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const run = useCallback(async (channel, payload, okMsg) => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const out = await window.zignagenixAPI.invoke(channel, payload);
      // ⛔ The server's own message, so NOT_VERIFIABLE and a missing field do
      // not collapse into the same shrug.
      if (out?.error) { setErr(out.message || out.code || 'Refused.'); return null; }
      setOk(okMsg);
      if (typeof onDone === 'function') onDone(out);
      return out;
    } catch (e) {
      setErr(`Could not reach the server: ${e.message}. Nothing was changed.`);
      return null;
    } finally { setBusy(false); }
  }, [onDone]);
  return { run, busy, err, ok, setErr };
}

function ActionResult({ err, ok }) {
  if (err) return <div role="alert" style={{ color: '#991B1B', fontSize: 13, marginTop: 6 }}>{err}</div>;
  if (ok) return <div role="status" style={{ color: '#166534', fontSize: 13, marginTop: 6 }}>{ok}</div>;
  return null;
}

const fld = { fontSize: 13, padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db' };

function DataTable({ columns, rows, emptyText, failed }) {
  /*
   * ⛔⛔ A FAILED LOAD IS NOT AN EMPTY ONE — #686, applied here.
   *
   * Every tab on this page rendered its ErrorBanner and then, because `rows`
   * was still [], rendered "No data subject requests on file." underneath it.
   * On a compliance screen that reads as "this clinic has received no erasure
   * requests", which is a statement about the law being satisfied, not about
   * a fetch failing. The banner alone is not enough while the page is also
   * asserting the opposite one line below.
   */
  if (failed) return null;
  if (!rows || rows.length === 0) {
    return <EmptyState>{emptyText || 'No records yet.'}</EmptyState>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${NEU.color?.border || '#E8ECF1'}` }}>
            {columns.map(c => (
              <th key={c.key} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', fontWeight: 600, color: '#374151' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i} style={{ borderBottom: '1px solid #F3F4F6' }}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: '0.5rem 0.75rem', color: '#4b5563' }}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
/*
 * DSR actions. ⛔ NO ERASURE BUTTON IN THIS PR — Daniel's instruction. Erasure
 * is destructive and its server-side safeguards (a reason, and a second-person
 * check so the verifier cannot also execute) land first, in their own card.
 */
function DsrActions({ onChanged }) {
  const [subject, setSubject] = useState('');
  const [type, setType] = useState('access');
  const [targetId, setTargetId] = useState('');
  const [erasing, setErasing] = useState(null);
  const { run, busy, err, ok } = useAction(onChanged);
  const idNum = parseInt(targetId, 10);
  const idOk = Number.isFinite(idNum) && idNum > 0;

  // ⚠ Both client rules have server counterparts: dsr:create refuses without
  // subjectIdentifier and requestType; the request_type values below are the
  // database's own CHECK constraint on data_subject_requests.
  return (
    <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <label style={{ fontSize: 12.5 }}>Subject (email or id)<br />
        <input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
          style={{ ...fld, width: 210 }} />
      </label>
      <label style={{ fontSize: 12.5 }}>Request type<br />
        <select aria-label="Request type" value={type} onChange={(e) => setType(e.target.value)} style={fld}>
          {['access', 'erasure', 'rectification', 'portability', 'restriction', 'objection']
            .map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <button type="button" style={btnSecondary} disabled={!subject.trim() || busy}
        onClick={() => run('compliance:dsr:create',
          { subjectIdentifier: subject.trim(), requestType: type }, 'Request filed.')}>
        New request
      </button>

      <span style={{ width: 1, alignSelf: 'stretch', background: '#E8ECF1', margin: '0 4px' }} />

      <label style={{ fontSize: 12.5 }}>Request ID<br />
        <input aria-label="Request ID" value={targetId} onChange={(e) => setTargetId(e.target.value)}
          inputMode="numeric" style={{ ...fld, width: 90 }} />
      </label>
      <button type="button" style={btnSecondary} disabled={!idOk || busy}
        onClick={() => run('compliance:dsr:verify-identity', { id: idNum }, 'Identity verified.')}>
        Verify identity
      </button>
      <button type="button" style={btnSecondary} disabled={!idOk || busy}
        onClick={() => run('compliance:dsr:export-data', { id: idNum }, 'Export prepared.')}>
        Export data
      </button>
      {/* ⚠ Legacy requests only. execute-erasure refuses when no verifier was
          ever recorded (VERIFIER_UNKNOWN); this is how a DIFFERENT administrator
          puts a name against one so it can proceed with two people. The server
          refuses to touch a request that already has a verifier. */}
      <button type="button" style={btnSecondary} disabled={!idOk || busy}
        onClick={() => {
          const why = window.prompt(
            'How was this request\u2019s identity established? (recorded against the request)');
          if (why == null) return;
          run('compliance:dsr:reverify-legacy', { id: idNum, reason: why },
            'Re-verified. A different administrator must now run the erasure.');
        }}>
        Re-verify (legacy)
      </button>
      <button type="button" disabled={!idOk || busy} onClick={() => setErasing(idNum)}
        style={{ ...btnSecondary, color: '#B91C1C' }}>
        Erase…
      </button>
      {erasing != null && (
        <EraseDialog dsrId={erasing} onClose={() => setErasing(null)}
          onDone={() => { setErasing(null); if (typeof onChanged === 'function') onChanged(); }} />
      )}
      <div style={{ flexBasis: '100%' }}><ActionResult err={err} ok={ok} /></div>
    </div>
  );
}

/*
 * ⛔⛔ THE ERASURE DIALOG — P128 PR-2.
 *
 * This is the only irreversible action in the compliance surface. The flow is
 * the server's, surfaced: DRY RUN FIRST (no confirm ⇒ the handler previews and
 * destroys nothing), then the operator sees the real counts, types a reason and
 * types the DSR id back, and only then does a confirmed call go out.
 *
 * ⛔ EVERY RULE HERE HAS A SERVER COUNTERPART, because a dialog is not a
 * control. The handler refuses on its own: SECOND_PERSON_REQUIRED when the
 * administrator who verified identity is the one executing, REASON_REQUIRED
 * below 10 characters, and it previews rather than deletes without `confirm`.
 * The typed-id confirmation is the ONE rule that is UI-only, and it is a
 * mis-click guard rather than a safeguard — it is deliberately not presented
 * as the thing standing between an operator and a deletion.
 */
function EraseDialog({ dsrId, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [typedId, setTypedId] = useState('');
  const { run, busy, err, ok } = useAction(null);

  const reasonOk = reason.trim().length >= 10;     // mirrors the handler's 10
  const idOk = typedId.trim() === String(dsrId);

  // ⚠ The dry run needs the reason too — the handler requires it on BOTH paths,
  // so that the stated reason is the one the operator decided ON, not one
  // typed afterwards to satisfy a final prompt.
  async function doPreview() {
    const out = await run('compliance:dsr:execute-erasure',
      { id: dsrId, reason: reason.trim() }, null);
    if (out) setPreview(out);
  }

  async function doErase() {
    const out = await run('compliance:dsr:execute-erasure',
      { id: dsrId, reason: reason.trim(), confirm: true }, 'Erasure executed. A receipt has been generated.');
    if (out && typeof onDone === 'function') onDone(out);
  }

  const counts = preview && preview.wouldDelete;

  return (
    <div className="modal-backdrop" role="presentation">
      <div role="alertdialog" aria-modal="true" aria-labelledby="erase-title" className="modal" style={{ maxWidth: 520 }}>
        <h3 id="erase-title" style={{ margin: '0 0 8px', fontSize: 16 }}>Erase the subject's records</h3>
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: '0 0 10px' }}>
          Request <strong>#{dsrId}</strong>. This <strong>permanently deletes</strong> the
          subject's contact and patient records and their linked rows, redacts the audit and
          PHI-access logs, and asks GoHighLevel to delete its copy. <strong>It cannot be undone.</strong>
        </p>

        <label htmlFor="erase-reason" style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
          Why is this erasure being executed?
        </label>
        <textarea id="erase-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Verified Art. 17 request from the data subject" style={{ ...fld, width: '100%' }} />
        <p style={{ fontSize: 11.5, color: reasonOk ? '#6C757D' : '#B45309', margin: '4px 0 10px' }}>
          {reasonOk ? 'Stored on the receipt and in the audit log.' : 'At least 10 characters.'}
        </p>

        {!preview ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-xs" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-xs" onClick={doPreview} disabled={!reasonOk || busy} style={btnSecondary}>
              {busy ? 'Checking…' : 'Preview what would be deleted'}
            </button>
          </div>
        ) : (
          <>
            {/* ⚠ COUNTS ONLY — never the subject's name, email or chart. The
                operator already knows who they looked up; the dialog does not
                need to restate a person's identity to confirm a deletion. */}
            <div data-testid="erase-preview" style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', fontSize: 13, margin: '0 0 10px' }}>
              This will delete <strong>{(counts && counts.crm_contacts) || 0}</strong> contact
              record(s) and <strong>{(counts && counts.patients) || 0}</strong> patient record(s).
            </div>
            <label htmlFor="erase-confirm" style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>
              Type the request id <strong>{dsrId}</strong> to confirm
            </label>
            <input id="erase-confirm" value={typedId} onChange={(e) => setTypedId(e.target.value)}
              style={{ ...fld, width: 120 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" className="btn-xs" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" className="btn-xs" onClick={doErase} disabled={!reasonOk || !idOk || busy}
                style={{ ...btnSecondary, background: (!reasonOk || !idOk || busy) ? '#e5e7eb' : '#DC2626', color: (!reasonOk || !idOk || busy) ? '#9ca3af' : '#fff' }}>
                {busy ? 'Erasing…' : 'Erase permanently'}
              </button>
            </div>
          </>
        )}
        <ActionResult err={err} ok={ok} />
      </div>
    </div>
  );
}

function DsrTab({ user }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const out = await window.zignagenixAPI.invoke('compliance:dsr:list');
      if (out?.error) setError(out.message || out.code);
      else setRows(Array.isArray(out) ? out : (out?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <Section title="Data Subject Requests (GDPR Art. 15/17/20, CCPA §1798.100)" icon={FileText}>
        <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
          Inbound requests from data subjects (patients, leads, contacts) to access, export, or erase
          their personal data. List, create-request, identity verification, cascading erasure,
          data-export ZIP packaging, and digital receipt generation are all <LiveBadge />.
        </p>
        <ErrorBanner error={error} />
        {loading ? <EmptyState>Loading…</EmptyState> : (
          <DataTable
            emptyText="No data subject requests on file."
            columns={[
              { key: 'id',           label: 'ID' },
              { key: 'request_type', label: 'Type' },
              { key: 'subject_email', label: 'Subject' },
              { key: 'status',       label: 'Status' },
              { key: 'created_at',   label: 'Filed',
                render: r => r.created_at ? new Date(r.created_at).toLocaleString() : '—' },
              { key: 'deadline_at',  label: 'Deadline',
                render: r => r.deadline_at ? new Date(r.deadline_at).toLocaleDateString() : '—' },
            ]}
            rows={rows}
            failed={!!error}
          />
        )}
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: 8 }}>
          <button onClick={load} style={btnSecondary}>
            <RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh
          </button>
        </div>
        {canWriteCompliance(user) && <DsrActions onChanged={load} />}
      </Section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
function IncidentActions({ onChanged }) {
  const [desc, setDesc] = useState('');
  const [sev, setSev] = useState('medium');
  const [targetId, setTargetId] = useState('');
  const [newStatus, setNewStatus] = useState('contained');
  const { run, busy, err, ok } = useAction(onChanged);
  const idNum = parseInt(targetId, 10);
  const idOk = Number.isFinite(idNum) && idNum > 0;

  // ⚠ Server counterparts: incident:declare refuses without description and
  // severity; the severity and status values below are the database's own
  // CHECK constraints on security_incidents.
  return (
    <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <label style={{ fontSize: 12.5 }}>What happened<br />
        <input aria-label="What happened" value={desc} onChange={(e) => setDesc(e.target.value)}
          placeholder="Laptop lost in transit" style={{ ...fld, width: 260 }} />
      </label>
      <label style={{ fontSize: 12.5 }}>Severity<br />
        <select aria-label="Severity" value={sev} onChange={(e) => setSev(e.target.value)} style={fld}>
          {['low', 'medium', 'high', 'critical'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <button type="button" style={btnSecondary} disabled={!desc.trim() || busy}
        onClick={() => run('compliance:incident:declare',
          { description: desc.trim(), severity: sev }, 'Incident declared — the 72h GDPR clock starts now.')}>
        Declare incident
      </button>

      <span style={{ width: 1, alignSelf: 'stretch', background: '#E8ECF1', margin: '0 4px' }} />

      <label style={{ fontSize: 12.5 }}>Incident ID<br />
        <input aria-label="Incident ID" value={targetId} onChange={(e) => setTargetId(e.target.value)}
          inputMode="numeric" style={{ ...fld, width: 90 }} />
      </label>
      <label style={{ fontSize: 12.5 }}>Set status<br />
        <select aria-label="Set status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} style={fld}>
          {['open', 'contained', 'notified', 'closed'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </label>
      <button type="button" style={btnSecondary} disabled={!idOk || busy}
        onClick={() => run('compliance:incident:update', { id: idNum, status: newStatus }, 'Incident updated.')}>
        Update
      </button>
      <div style={{ flexBasis: '100%' }}><ActionResult err={err} ok={ok} /></div>
    </div>
  );
}

function IncidentTab({ user }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Breach-resolver state (Task #173) — hidden entirely without the perm.
  const [cohort, setCohort] = useState(null);
  const [cohortId, setCohortId] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [cohortError, setCohortError] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [genMsg, setGenMsg] = useState(null);

  const canResolve = Array.isArray(user?.permissions) && user.permissions.includes('privacy:breach:resolve');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const out = await window.zignagenixAPI.invoke('compliance:incident:list');
      if (out?.error) setError(out.message || out.code);
      else setRows(Array.isArray(out) ? out : (out?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = useCallback(async (id) => {
    setCohortLoading(true); setCohortError(null); setCohort(null); setCohortId(id); setGenMsg(null);
    try {
      const out = await window.zignagenixAPI.invoke('compliance:incident:list-affected-subjects', { id });
      if (out?.error) setCohortError(out.message || out.code);
      else setCohort(out);
    } catch (e) { setCohortError(e.message); }
    finally { setCohortLoading(false); }
  }, []);

  const generate = useCallback(async (id) => {
    setGenLoading(true); setGenMsg(null);
    try {
      const out = await window.zignagenixAPI.invoke('compliance:breach:generate-notices', { id });
      if (out?.error) { setGenMsg({ type: 'error', text: out.message || out.code }); return; }
      // base64 → Blob → download (nothing persisted server-side).
      const bin = atob(out.zipBase64); const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/zip' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = out.filename; a.click();
      URL.revokeObjectURL(a.href);
      setGenMsg({ type: 'success', text: `Generated ${out.subjectCount} DRAFT letter(s)` +
        (out.missingAddressCount ? ` — ${out.missingAddressCount} with [ADDRESS MISSING]` : '') + '. Downloaded.' });
    } catch (e) { setGenMsg({ type: 'error', text: e.message }); }
    finally { setGenLoading(false); }
  }, []);

  const columns = [
    { key: 'id',         label: 'ID' },
    { key: 'severity',   label: 'Severity' },
    { key: 'description', label: 'Description' },
    { key: 'status',     label: 'Status' },
    { key: 'declared_at', label: 'Declared',
      render: r => r.declared_at ? new Date(r.declared_at).toLocaleString() : '—' },
    { key: 'gdpr_72h_deadline', label: 'GDPR 72h',
      render: r => r.gdpr_72h_deadline ? new Date(r.gdpr_72h_deadline).toLocaleString() : '—' },
  ];
  if (canResolve) {
    columns.push({ key: '_resolve', label: 'Breach cohort',
      render: r => (
        <button onClick={() => resolve(r.id)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>
          <Search size={12} style={{ marginRight: 4 }} /> Find affected subjects
        </button>
      ) });
  }

  return (
    <Section title="Security Incidents & Breach Workflow (HIPAA §164.404, GDPR Art. 33)" icon={AlertCircle}>
      <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
        Declared security incidents and the notification clock. Declare, update, list, and the
        affected-subjects resolver + FEDERAL notice letters are <LiveBadge />
        {canResolve ? '' : ' (breach-resolver controls require the privacy:breach:resolve permission)'}.
        State-law notice variants are deferred.
      </p>
      <ErrorBanner error={error} />
      {loading ? <EmptyState>Loading…</EmptyState> : (
        <DataTable
          emptyText="No security incidents have been declared. (A clean board is the goal.)"
          columns={columns}
          rows={rows}
          failed={!!error}
        />
      )}
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: 8 }}>
        <button onClick={load} style={btnSecondary}>
          <RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh
        </button>
      </div>

      {canResolve && (cohortLoading || cohortError || cohort) && (
        <BreachCohortPanel
          id={cohortId} cohort={cohort} loading={cohortLoading} error={cohortError}
          genLoading={genLoading} genMsg={genMsg} onGenerate={generate}
        />
      )}
      {canWriteCompliance(user) && <IncidentActions onChanged={load} />}
    </Section>
  );
}

// Affected-subjects results panel (Task #173). Renders the two evidence tiers
// distinctly, the jurisdiction breakdown (UNKNOWN highlighted), threshold flags,
// the 60-day clock, blind-spots, any truncation warning, and the DRAFT letter
// generator. Presentation only over the resolver payload.
export function BreachCohortPanel({ id, cohort, loading, error, genLoading, genMsg, onGenerate }) {
  if (loading) return <div style={{ marginTop: 16 }}><EmptyState>Resolving affected subjects for incident #{id}…</EmptyState></div>;
  if (error) return <div style={{ marginTop: 16 }}><ErrorBanner error={error} /></div>;
  if (!cohort) return null;
  const s = cohort.summary || {};
  const clock = cohort.clock || {};
  const th = cohort.thresholds || {};
  const card = { background: '#F8F9FA', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginTop: 16 };
  const tierChip = (label, color) => (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: color + '22', color, marginLeft: 6 }}>{label}</span>
  );
  return (
    <div style={card} data-testid="breach-cohort-panel">
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Affected subjects — incident #{id}</div>

      {cohort.truncated && (
        <div data-testid="truncated-warning" style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8, padding: '8px 10px', fontSize: 12, marginBottom: 10 }}>
          ⚠ Result truncated at the safety cap ({cohort.truncationDetail?.cap}). Counts below are a floor, not the full cohort.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ ...card, marginTop: 0, flex: '1 1 180px' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Patients {tierChip('AUTHORITATIVE', '#16a34a')}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{s.patient_count ?? 0}</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>phi_access_log</div>
        </div>
        <div style={{ ...card, marginTop: 0, flex: '1 1 180px' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Contacts {tierChip('DERIVED — WEAKER', '#d97706')}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{s.direct_contact_count ?? 0}</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>audit_log (crm_contacts)</div>
        </div>
        <div style={{ ...card, marginTop: 0, flex: '1 1 180px' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Indirect buckets {tierChip('UNRESOLVED', '#6b7280')}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{s.indirect_exposure_buckets ?? 0}</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>volume only</div>
        </div>
      </div>

      {/* 60-day clock + HIPAA threshold flags */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 12 }}>
        <span style={{ padding: '4px 10px', borderRadius: 6, background: clock.overdue ? '#fef2f2' : '#f0f9ff', color: clock.overdue ? '#991b1b' : '#0369a1', fontWeight: 600 }}>
          60-day individual notice: {clock.overdue ? `OVERDUE by ${-clock.daysRemaining}d` : `${clock.daysRemaining}d remaining`} (due {clock.noticeDeadline ? new Date(clock.noticeDeadline).toLocaleDateString() : '—'})
        </span>
        {th.hhsContemporaneousNotice && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#fffbeb', color: '#92400e', fontWeight: 600 }}>500+ individuals → contemporaneous HHS + media notice</span>}
        {Array.isArray(th.mediaNotice) && th.mediaNotice.length > 0 && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#fffbeb', color: '#92400e', fontWeight: 600 }}>Media notice: {th.mediaNotice.join(', ')}</span>}
      </div>

      {/* Jurisdiction breakdown (patient cohort) */}
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>By jurisdiction (patients)</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {(cohort.byJurisdiction || []).map(j => (
          <span key={j.code} data-testid={`jur-${j.code}`} style={{
            fontSize: 12, padding: '2px 8px', borderRadius: 6,
            background: j.code === 'UNKNOWN' ? '#fef2f2' : '#eef2f7',
            color: j.code === 'UNKNOWN' ? '#991b1b' : '#374151',
            border: j.code === 'UNKNOWN' ? '1px solid #fca5a5' : '1px solid #e5e7eb',
            fontWeight: j.code === 'UNKNOWN' ? 700 : 500,
          }}>{j.code}: {j.count}</span>
        ))}
        {(cohort.byJurisdiction || []).length === 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}>No patient jurisdictions.</span>}
      </div>

      {/* Blind spots */}
      {Array.isArray(cohort.blindSpots) && cohort.blindSpots.length > 0 && (
        <details style={{ marginBottom: 12 }} data-testid="blind-spots">
          <summary style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#92400e' }}>
            Paths this resolver cannot see ({cohort.blindSpots.length})
          </summary>
          <ul style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 0', paddingLeft: 18 }}>
            {cohort.blindSpots.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
          </ul>
        </details>
      )}

      <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>{s.note}</div>

      {/* DRAFT letter generation */}
      {genMsg && (
        <div style={{ fontSize: 12, marginBottom: 8, color: genMsg.type === 'error' ? '#991b1b' : '#166534' }}>{genMsg.text}</div>
      )}
      <button onClick={() => onGenerate(id)} disabled={genLoading || (cohort.summary?.patient_count ?? 0) === 0}
        style={{ ...btnSecondary, opacity: genLoading || (cohort.summary?.patient_count ?? 0) === 0 ? 0.5 : 1 }}>
        <Download size={14} style={{ marginRight: 4 }} /> {genLoading ? 'Generating…' : 'Generate notice letters (DRAFT)'}
      </button>
      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
        Federal §164.404 letters, one per patient. DRAFT placeholder legal text + watermark — counsel must review before use.
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
/*
 * ⛔⛔ THE TOP-PRIORITY FORM ON THIS PAGE, AND THE REASON IS NOT COSMETIC.
 *
 * database/trainingGate.js:86 BLOCKS LOGIN with TRAINING_OVERDUE_BLOCKED once a
 * required training is more than 7 days lapsed, and LoginPage.js:221 renders
 * that refusal. Enforcement shipped; RECORDING never did — compliance:training:record
 * had no caller anywhere (P123). So a clinician could be locked out of the
 * system by a record no screen existed to create, during a parallel run.
 *
 * ⚠ The category picker offers only the three the gate evaluates. Recording
 * any other category succeeds and clears NOTHING — the admin would be told it
 * worked while the person stayed locked out.
 */
/*
 * ⛔ A NAME SEARCH, NOT A USER ID — Card P135.
 *
 * The training form shipped asking for a numeric user id. That is honest
 * about what compliance:training:record accepts and useless to an
 * administrator who knows a name, and it was the first thing I expected a
 * trainer to hit on 8 Nov.
 *
 * ⚠ The PAYLOAD is unchanged: this resolves a name to the same numeric id the
 * handler has always taken. The server is not relaxed to accept names — that
 * would move identity resolution to a place with no audit trail.
 */
function StaffPicker({ label, value, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const { run, busy, err } = useAction(null);

  async function search(term) {
    setQ(term);
    if (value) onPick(null);              // typing again clears the selection
    if (term.trim().length < 2) { setRows(null); setTruncated(false); return; }
    const out = await run('compliance:staff:search', { q: term.trim() }, null);
    if (out) { setRows(out.results || []); setTruncated(!!out.truncated); }
  }

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ fontSize: 12.5 }}>{label}<br />
        <input
          aria-label={label}
          value={value ? value.display_name : q}
          onChange={(e) => search(e.target.value)}
          placeholder="Type a name"
          style={{ ...fld, width: 210 }}
        />
      </label>
      {busy && <span style={{ fontSize: 11.5, color: '#6C757D', marginLeft: 6 }}>searching…</span>}
      <ActionResult err={err} ok={null} />
      {!value && rows && (
        <ul data-testid="staff-results" style={{
          listStyle: 'none', margin: '4px 0 0', padding: 0, maxHeight: 160, overflowY: 'auto',
          border: rows.length ? '1px solid #d1d5db' : 'none', borderRadius: 6, background: '#fff',
        }}>
          {rows.length === 0 && (
            <li style={{ fontSize: 12.5, color: '#6C757D', padding: '4px 2px' }}>
              No active staff match that name.
            </li>
          )}
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => { onPick(r); setRows(null); }}
                style={{ ...btnSecondary, border: 'none', width: '100%', textAlign: 'left', background: 'none' }}>
                {r.display_name} <span style={{ color: '#6C757D' }}>· {r.role}</span>
              </button>
            </li>
          ))}
          {/* ⚠ Said out loud: 20 of 40 shown as if it were all of them is how
              training gets recorded against the wrong person of the same name. */}
          {truncated && (
            <li style={{ fontSize: 11.5, color: '#B45309', padding: '4px 2px' }}>
              Showing the first 20 matches — narrow the search.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function RecordTrainingForm({ onRecorded }) {
  const [staff, setStaff] = useState(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState(GATE_CATEGORIES[0]);
  const [completedAt, setCompletedAt] = useState('');
  const { run, busy, err, ok } = useAction(onRecorded);

  // ⚠ Each of these has a SERVER counterpart: the handler refuses without
  // userId, trainingName and trainingCategory. A client rule with no server
  // rule is decoration.
  const idNum = staff ? staff.id : NaN;
  const invalid = !Number.isFinite(idNum) || idNum <= 0 || !name.trim() || !category;

  async function submit(e) {
    e.preventDefault();
    if (invalid || busy) return;
    const out = await run('compliance:training:record', {
      userId: idNum,
      trainingName: name.trim(),
      trainingCategory: category,
      completedAt: completedAt || undefined,
    }, GATE_CATEGORIES.includes(category)
      ? `Recorded — this lifts the login block for ${category}.`
      // ⚠ Say so plainly. A "Recorded ✓" on a non-gate category, given to an
      // admin who is trying to unlock someone, is a true statement that
      // answers the wrong question.
      : `Recorded. Note: ${category} is not one of the categories the login gate checks, so this does not unlock anyone.`);
    if (out) { setName(''); setCompletedAt(''); setStaff(null); }
  }

  return (
    <form onSubmit={submit} style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <StaffPicker label="Staff member" value={staff} onPick={setStaff} />
      <label style={{ fontSize: 12.5 }}>Training name<br />
        <input aria-label="Training name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="HIPAA Annual 2026" style={{ ...fld, width: 200 }} />
      </label>
      <label style={{ fontSize: 12.5 }}>Category<br />
        <select aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)} style={fld}>
          {ALL_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}{GATE_CATEGORIES.includes(c) ? ' — clears login block' : ''}
            </option>
          ))}
        </select>
      </label>
      <label style={{ fontSize: 12.5 }}>Completed<br />
        <input aria-label="Completed" type="date" value={completedAt}
          onChange={(e) => setCompletedAt(e.target.value)} style={fld} />
      </label>
      <button type="submit" disabled={invalid || busy} style={btnSecondary}>
        {busy ? 'Recording…' : 'Record training'}
      </button>
      <div style={{ flexBasis: '100%' }}><ActionResult err={err} ok={ok} /></div>
    </form>
  );
}

/** Per-user gate status — the SAME evaluation sign-in applies. */
function TrainingStatusLine() {
  const [staff, setStaff] = useState(null);
  const [status, setStatus] = useState(null);
  const { run, busy, err } = useAction((out) => setStatus(out));
  const idNum = staff ? staff.id : NaN;

  return (
    <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <StaffPicker label="Check a staff member"
        value={staff} onPick={(r) => { setStaff(r); setStatus(null); }} />
      <button type="button" disabled={!Number.isFinite(idNum) || idNum <= 0 || busy} style={btnSecondary}
        onClick={() => run('compliance:training:check-status', { userId: idNum })}>
        {busy ? 'Checking…' : 'Check status'}
      </button>
      <div style={{ flexBasis: '100%' }}>
        <ActionResult err={err} ok={null} />
        {status && (
          <p data-testid="training-status" style={{ fontSize: 13, margin: '6px 0 0', color: status.wouldBlockLogin ? '#991B1B' : '#166534' }}>
            User {status.userId}: <strong>{status.gateStatus}</strong>
            {status.wouldBlockLogin ? ' — login is BLOCKED' : ' — login is not blocked'}
            {status.gateDisabled ? ' (gate disabled)' : ''}
            {Array.isArray(status.overdueTrainings) && status.overdueTrainings.length
              ? ` · overdue: ${status.overdueTrainings.map((t) => t.category).join(', ')}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

function TrainingTab({ user }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const out = await window.zignagenixAPI.invoke('compliance:training:list');
      if (out?.error) setError(out.message || out.code);
      else setRows(Array.isArray(out) ? out : (out?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Section title="Workforce Training Records (MA 201 CMR 17.04(8), GDPR Art. 39)" icon={Users}>
      <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
        Per-user training completion records. List + record handlers, and the
        overdue-training <strong>login gate</strong>, are <LiveBadge />. Policy: a
        required training that has lapsed (expired) gives a <strong>7-day grace</strong>
        window of warning logins; past 7 days overdue, login is blocked until the
        training is recorded. The reserved System Administrator account is exempt.
      </p>
      <ErrorBanner error={error} />
      {loading ? <EmptyState>Loading…</EmptyState> : (
        <DataTable
          emptyText="No training records on file yet."
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'user_id', label: 'User' },
            { key: 'training_code', label: 'Training' },
            { key: 'completed_at', label: 'Completed',
              render: r => r.completed_at ? new Date(r.completed_at).toLocaleDateString() : '—' },
            { key: 'expires_at', label: 'Expires',
              render: r => r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '—' },
          ]}
          rows={rows}
          failed={!!error}
        />
      )}
      {canWriteCompliance(user) && (
        <>
          <RecordTrainingForm onRecorded={load} />
          <TrainingStatusLine />
        </>
      )}
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: 8 }}>
        <button onClick={load} style={btnSecondary}>
          <RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh
        </button>
      </div>
    </Section>
  );
}

// ───────────────────────────────────────────────────────────────────────
function RopaTab() {
  const [dpas, setDpas] = useState([]);
  const [ropa, setRopa] = useState([]);
  const [privacyNotices, setPrivacyNotices] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [d, r, pn] = await Promise.all([
        window.zignagenixAPI.invoke('compliance:dpa:list'),
        window.zignagenixAPI.invoke('compliance:ropa:list'),
        window.zignagenixAPI.invoke('compliance:privacy-notice:list-versions'),
      ]);
      const errs = [d, r, pn].filter(x => x?.error).map(x => x.message || x.code);
      if (errs.length) setError(errs.join('; '));
      setDpas(Array.isArray(d) ? d : (d?.data || []));
      setRopa(Array.isArray(r) ? r : (r?.data || r?.activities || []));
      setPrivacyNotices(Array.isArray(pn) ? pn : (pn?.data || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <Section title="Records of Processing Activities (GDPR Art. 30)" icon={Database}>
        <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
          The RoPA inventory — every processing activity ZignaGenix performs on personal data,
          its lawful basis, retention period, and data categories. Sourced from{' '}
          <code style={{ background: '#F3F4F6', padding: '1px 5px', borderRadius: 4 }}>
            database/dataProcessingRegistry.js
          </code>. List handler is <LiveBadge />.
        </p>
        <ErrorBanner error={error} />
        {loading ? <EmptyState>Loading…</EmptyState> : (
          <DataTable
            emptyText="No processing activities declared."
            columns={[
              { key: 'name', label: 'Activity',
                render: r => r.name || r.activity || r.title },
              { key: 'lawful_basis', label: 'Lawful basis',
                render: r => r.lawful_basis || r.basis || '—' },
              { key: 'data_categories', label: 'Data categories',
                render: r => Array.isArray(r.data_categories) ? r.data_categories.join(', ') : (r.data_categories || '—') },
              { key: 'retention_period', label: 'Retention',
                render: r => r.retention_period || r.retention || '—' },
            ]}
            rows={ropa}
            failed={!!error}
          />
        )}
      </Section>

      <Section title="Data Processing Agreements (sub-processor inventory)" icon={FileCheck}>
        <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
          Signed DPAs with each third party that processes personal data on our behalf
          (AWS, Stripe, Twilio, Anthropic via Bedrock, etc.). List + upsert handlers
          are <LiveBadge />.
        </p>
        {loading ? <EmptyState>Loading…</EmptyState> : (
          <DataTable
            emptyText="No DPAs on file yet."
            columns={[
              { key: 'vendor_name', label: 'Vendor' },
              { key: 'service',     label: 'Service' },
              { key: 'dpa_signed_at', label: 'Signed',
                render: r => r.dpa_signed_at ? new Date(r.dpa_signed_at).toLocaleDateString() : '—' },
              { key: 'gdpr_compliant', label: 'GDPR',
                render: r => r.gdpr_compliant ? '✓' : '—' },
              { key: 'ccpa_compliant', label: 'CCPA',
                render: r => r.ccpa_compliant ? '✓' : '—' },
            ]}
            rows={dpas}
            failed={!!error}
          />
        )}
      </Section>

      <Section title="Privacy Notice Versions" icon={BookOpen}>
        <p style={{ fontSize: 13, color: '#4b5563', margin: '0 0 0.75rem' }}>
          Versioned record of the public privacy notice. Required for GDPR Art. 13/14
          and CCPA §1798.130. List handler is <LiveBadge />.
        </p>
        {loading ? <EmptyState>Loading…</EmptyState> : (
          <DataTable
            emptyText="No privacy notice versions recorded yet."
            columns={[
              { key: 'version',      label: 'Version' },
              { key: 'effective_at', label: 'Effective',
                render: r => r.effective_at ? new Date(r.effective_at).toLocaleDateString() : '—' },
              { key: 'created_by',   label: 'Created by' },
              { key: 'is_active',    label: 'Active',
                render: r => r.is_active ? '✓' : '—' },
            ]}
            rows={privacyNotices}
            failed={!!error}
          />
        )}
      </Section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
const btnSecondary = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  background: '#FFFFFF', color: '#374151', fontSize: 13, fontWeight: 500,
  cursor: 'pointer',
};

const TABS = [
  { id: 'dsr',       label: 'Data Subject Rights',   icon: FileText,    Component: DsrTab },
  { id: 'incidents', label: 'Incidents & Breaches', icon: AlertCircle, Component: IncidentTab },
  { id: 'training',  label: 'Workforce Training',   icon: Users,       Component: TrainingTab },
  { id: 'ropa',      label: 'RoPA / DPA / Privacy', icon: Database,    Component: RopaTab },
];

export default function CompliancePage({ user }) {
  const [activeTab, setActiveTab] = useState('dsr');
  const Active = TABS.find(t => t.id === activeTab)?.Component || DsrTab;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
        <ShieldAlert size={28} color="#2E8B98" />
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#1f2937' }}>Privacy & Compliance</h2>
          <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
            {/* ⚠ P135: this said "Tasks #172-#175 ship the full UI; this page surfaces
                the schema + IPC scaffolding already in place." Those tasks shipped, and
                P128 wired the write actions, so the sentence was describing a page that
                no longer existed — telling an administrator that what is in front of
                them is scaffolding. */}
            GDPR / CCPA / HIPAA control surface. File and track data-subject requests,
            declare incidents, and record workforce training.
          </p>
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 4, marginBottom: '1rem',
        borderBottom: `1px solid ${NEU.color?.border || '#E8ECF1'}`,
      }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '0.55rem 0.95rem', border: 'none',
                background: 'transparent', cursor: 'pointer',
                borderBottom: active ? '2px solid #2E8B98' : '2px solid transparent',
                color: active ? '#2E8B98' : '#4b5563',
                fontWeight: active ? 600 : 500, fontSize: 14,
              }}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      <Active user={user} />
    </div>
  );
}
